import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const partnerSchema = z.object({
  nombre: z.string().trim().min(1, "Razón social requerida").max(200),
  nit: z.string().trim().max(50).optional().or(z.literal("")),
  contacto: z.string().trim().max(100).optional().or(z.literal("")),
  email: z.string().trim().email("Email inválido").max(255).optional().or(z.literal("")),
  telefono: z.string().trim().max(30).optional().or(z.literal("")),
  direccion: z.string().trim().max(300).optional().or(z.literal("")),
  ciudad: z.string().trim().max(100).optional().or(z.literal("")),
  pais: z.string().trim().max(100).optional().or(z.literal("")),
  notas: z.string().trim().max(1000).optional().or(z.literal("")),
});

type PartnerFormValues = z.infer<typeof partnerSchema>;

interface PartnerFormModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  partner: {
    id: string;
    nombre: string;
    nit?: string | null;
    contacto?: string | null;
    email?: string | null;
    telefono?: string | null;
    direccion?: string | null;
    ciudad?: string | null;
    pais?: string | null;
    notas?: string | null;
  } | null;
  type: "proveedor" | "cliente";
}

export default function PartnerFormModal({ open, onOpenChange, partner, type }: PartnerFormModalProps) {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const table = type === "proveedor" ? "proveedores" : "clientes";

  const form = useForm<PartnerFormValues>({
    resolver: zodResolver(partnerSchema),
    values: {
      nombre: partner?.nombre ?? "",
      nit: partner?.nit ?? "",
      contacto: partner?.contacto ?? "",
      email: partner?.email ?? "",
      telefono: partner?.telefono ?? "",
      direccion: partner?.direccion ?? "",
      ciudad: partner?.ciudad ?? "",
      pais: partner?.pais ?? "",
      notas: partner?.notas ?? "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: PartnerFormValues) => {
      const payload = {
        nombre: values.nombre,
        nit: values.nit || null,
        contacto: values.contacto || null,
        email: values.email || null,
        telefono: values.telefono || null,
        direccion: values.direccion || null,
        ciudad: values.ciudad || null,
        pais: values.pais || null,
        notas: values.notas || null,
      };

      if (partner?.id) {
        const { error } = await supabase.from(table).update(payload).eq("id", partner.id);
        if (error) throw error;
      } else {
        if (!orgId) throw new Error("Sin organización");
        const { error } = await supabase.from(table).insert({ ...payload, org_id: orgId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [table] });
      queryClient.invalidateQueries({ queryKey: ["partner-stats"] });
      queryClient.invalidateQueries({ queryKey: ["partner-ops"] });
      queryClient.invalidateQueries({ queryKey: ["partner-activity"] });
      queryClient.invalidateQueries({ queryKey: ["partner-compliance"] });
      toast.success(partner?.id ? "Socio actualizado" : "Socio creado");
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error("Error al guardar", { description: (err as Error).message });
    },
  });

  const onSubmit = (values: PartnerFormValues) => mutation.mutate(values);

  const fields: { name: keyof PartnerFormValues; label: string; placeholder: string; half?: boolean }[] = [
    { name: "nombre", label: "Razón Social *", placeholder: "Nombre de la empresa" },
    { name: "nit", label: "NIT / Tax ID", placeholder: "900.123.456-7", half: true },
    { name: "contacto", label: "Contacto", placeholder: "Nombre del contacto", half: true },
    { name: "email", label: "Email", placeholder: "contacto@empresa.com", half: true },
    { name: "telefono", label: "Teléfono", placeholder: "+57 300 123 4567", half: true },
    { name: "direccion", label: "Dirección", placeholder: "Calle / Carrera" },
    { name: "ciudad", label: "Ciudad", placeholder: "Bogotá", half: true },
    { name: "pais", label: "País", placeholder: "Colombia", half: true },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {partner?.id ? "Editar" : "Nuevo"} {type === "proveedor" ? "Proveedor" : "Cliente"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <FormField
                  key={f.name}
                  control={form.control}
                  name={f.name}
                  render={({ field }) => (
                    <FormItem className={f.half ? "" : "col-span-2"}>
                      <FormLabel className="text-xs">{f.label}</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={f.placeholder} className="h-9" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>

            <FormField
              control={form.control}
              name="notas"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Observaciones internas..." rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {partner?.id ? "Guardar Cambios" : "Crear"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
